/**
 * BankTransferForm
 *
 * Displays virtual bank account details (SEPA/ACH) and a unique transfer
 * reference code. The user copies the details, makes the transfer via their
 * own bank, then clicks "I have made the transfer" to notify the platform.
 *
 * Props:
 *   referenceCode  {string}   — unique OT-YYYYMMDD-XXXXXX reference
 *   bankDetails    {object}   — { beneficiary, accounts: [...], support_email }
 *   pkg            {object}   — selected package { credits, price_cents, currency, label }
 *   purchaseId     {string}   — PendingPurchase UUID
 *   onSubmitted    {fn}       — called after user notifies transfer sent
 *   submitting     {bool}
 *   setSubmitting  {fn}
 */
import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Paper,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Send as SendIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

const fmt = (cents, currency = 'EUR') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(
    cents / 100,
  );

const CopyField = ({ label, value }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
      <Box flex={1} minWidth={0}>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        <Typography
          variant="body2"
          fontFamily="monospace"
          sx={{ wordBreak: 'break-all', fontWeight: 500 }}
        >
          {value || '—'}
        </Typography>
      </Box>
      <Tooltip title={copied ? 'Copied!' : 'Copy'}>
        <IconButton size="small" onClick={handleCopy} disabled={!value}>
          {copied ? (
            <CheckIcon fontSize="small" sx={{ color: 'success.main' }} />
          ) : (
            <CopyIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

const AccountBlock = ({ acct }) => (
  <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
      <Chip label={acct.payment_type} size="small" color="primary" variant="outlined" />
      <Chip label={acct.currency} size="small" variant="outlined" />
      {acct.bank_name && (
        <Typography variant="caption" color="text.secondary">
          via {acct.bank_name}
        </Typography>
      )}
    </Stack>

    <Stack spacing={1.2}>
      {acct.iban && <CopyField label="IBAN" value={acct.iban} />}
      {acct.bic && <CopyField label="BIC / SWIFT" value={acct.bic} />}
      {acct.routing && <CopyField label="Routing Number" value={acct.routing} />}
      {acct.account && <CopyField label="Account Number" value={acct.account} />}
    </Stack>
  </Paper>
);

const BankTransferForm = ({
  referenceCode,
  bankDetails,
  pkg,
  purchaseId,
  onSubmitted,
  submitting,
  setSubmitting,
}) => {
  const [localError, setLocalError] = useState(null);

  const handleSubmit = async () => {
    setSubmitting(true);
    setLocalError(null);
    try {
      await onSubmitted(purchaseId);
    } catch (err) {
      setLocalError(err?.message || 'Failed to record transfer notification.');
      setSubmitting(false);
    }
  };

  if (!bankDetails?.accounts?.length) {
    return (
      <Alert severity="warning">
        Payment is not yet configured. Please contact support.
      </Alert>
    );
  }

  const amountLabel = pkg ? fmt(pkg.price_cents, pkg.currency || 'eur') : '';

  return (
    <Box>
      {/* Reference code — most important */}
      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 2.5, bgcolor: 'primary.50', borderColor: 'primary.main' }}
      >
        <Typography variant="caption" color="primary.main" fontWeight={600} display="block" gutterBottom>
          Your transfer reference
        </Typography>
        <CopyField label="Include this code in the transfer description" value={referenceCode} />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          This is how we match your payment to your account. Do not omit it.
        </Typography>
      </Paper>

      {/* Beneficiary */}
      {bankDetails.beneficiary && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Beneficiary name
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            {bankDetails.beneficiary}
          </Typography>
        </Box>
      )}

      {/* Bank accounts */}
      <Typography variant="subtitle2" gutterBottom>
        Send payment to
      </Typography>
      {bankDetails.accounts.map((acct, i) => (
        <AccountBlock key={i} acct={acct} />
      ))}

      {/* Amount */}
      {pkg && (
        <Paper variant="outlined" sx={{ p: 1.5, mt: 1, mb: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Amount to transfer
            </Typography>
            <Typography variant="h6" fontWeight={700} color="primary.main">
              {amountLabel}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            For {pkg.credits} credits — {pkg.label} package
          </Typography>
        </Paper>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Info notice */}
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
        <InfoIcon fontSize="small" sx={{ color: 'text.secondary', mt: 0.1, flexShrink: 0 }} />
        <Typography variant="caption" color="text.secondary">
          Credits are added manually after we verify the incoming transfer — usually within 1 business day.
          {bankDetails.support_email && (
            <> Questions? <a href={`mailto:${bankDetails.support_email}`}>{bankDetails.support_email}</a></>
          )}
        </Typography>
      </Stack>

      {localError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLocalError(null)}>
          {localError}
        </Alert>
      )}

      <Button
        variant="contained"
        fullWidth
        size="large"
        startIcon={<SendIcon />}
        onClick={handleSubmit}
        disabled={submitting}
      >
        {submitting ? 'Recording…' : 'I have made the transfer'}
      </Button>
    </Box>
  );
};

export default BankTransferForm;
